/**
 * AI 机器人摆牌算法（对齐 g13suanfa / Program.java）
 *
 * 算法核心：
 * 1. getMaxCardType(hands) - 检测手牌中所有可能的牌型（含鬼牌组合）
 * 2. getAllResult(hands) - 递归排列三墩牌，确保头<=中<=尾
 * 3. isHandStrongerOrEqual - 同型牌逐张比牌面（含鬼牌弱化规则）
 * 4. deduplicateResults - 推荐方案去重
 *
 * 牌型等级（值越大牌型越大）：
 * 1=乌龙（高牌/散牌）, 2=对子, 3=两对, 4=三条, 5=顺子, 6=同花, 7=葫芦, 8=炸弹, 9=同花顺, 10=五条
 */

import { sortByRank, getRankCounts, getSuitCounts } from './poker'
import { 
  evaluateFiveCards, 
  evaluateThreeCards, 
  isValidLayout,
  compareFiveCards,
  compareThreeCards
} from './handEvaluator'

// 牌型枚举（与 g13suanfa DeckTypeEnum 一致）
const DeckTypeEnum = {
  Error: 255,
  None: 0,
  Single: 1,      // 乌龙（高牌/散牌）
  Double: 2,      // 对子
  TwoDouble: 3,   // 两对
  Three: 4,       // 三条
  ShunZi: 5,      // 顺子
  TongHua: 6,     // 同花
  Gourd: 7,       // 葫芦
  Bomb: 8,        // 炸弹（铁支）
  TongHuaShun: 9, // 同花顺
  FiveBomb: 10    // 五条
}

/** 牌面值：A=14（与 g13suanfa %100 编码一致），鬼牌不计 */
function getFaceValue(card) {
  if (!card || card.isJoker) return 0
  const v = Number(card.rank.value)
  return v === 1 ? 14 : v
}

/** 花色序：与 g13suanfa 一致 1红桃 2黑桃 3梅花 4方块；此处仅用于同点比花色 */
function getSuitOrder(card) {
  if (!card || card.isJoker) return 0
  const map = { hearts: 1, spades: 2, clubs: 3, diamonds: 4 }
  return map[card.suit.name] || 0
}

// AI摆牌主函数
export function aiArrange(hands) {
  const results = getAllResult(hands)
  if (results.length === 0) {
    return forceValidLayout(hands)
  }
  
  const candidates = results.map(r => ({
    front: r.typeCardList[2].cardList,
    middle: r.typeCardList[1].cardList,
    back: r.typeCardList[0].cardList
  }))
  
  const selected = selectBestCandidate(candidates)
  
  if (!selected) {
    return forceValidLayout(hands)
  }
  
  const allCards = [...selected.front, ...selected.middle, ...selected.back]
  if (allCards.length !== 13) {
    return forceValidLayout(hands)
  }
  
  const valid = isValidLayout(selected.front, selected.middle, selected.back)
  if (!valid.valid) {
    return forceValidLayout(hands)
  }
  
  return selected
}

// ==================== 参考方案核心算法 ====================

/**
 * 获取一个牌的排序值（用于比较）
 * 对齐 g13suanfa：先比牌面(A=14)，同点比花色
 */
function getSortValue(card) {
  if (card.isJoker) return card.jokerType === 'big' ? 10000 : 9900
  return getFaceValue(card) * 10 + getSuitOrder(card)
}

/**
 * 牌面从大到小排序
 */
function sortCardDesc(cards) {
  return [...cards].sort((a, b) => getSortValue(b) - getSortValue(a))
}

/**
 * 牌面从小到大排序
 */
function sortCardAsc(cards) {
  return [...cards].sort((a, b) => getSortValue(a) - getSortValue(b))
}

/**
 * 判断两张牌是否相同
 */
function isSameCard(c1, c2) {
  return c1.id === c2.id
}

/**
 * 判断牌是否是小鬼
 */
function isSmallJoker(card) {
  return card.isJoker && card.jokerType === 'small'
}

/**
 * 判断牌是否是大鬼
 */
function isBigJoker(card) {
  return card.isJoker && card.jokerType === 'big'
}

/**
 * 从列表中删除指定元素
 */
function deleteListElement(list, elements) {
  for (const el of elements) {
    const idx = list.findIndex(c => isSameCard(c, el))
    if (idx >= 0) list.splice(idx, 1)
  }
}

/**
 * 计算一副牌中所有可能的牌型
 * @param {Card[]} cardList - 牌组
 * @returns {{cardList: Card[], cardType: number}[]} 所有可能的牌型列表
 */
function getMaxCardType(cardList) {
  const typeCardList = []

  const sortedDesc = sortCardDesc(cardList)
  const sortedAsc = sortCardAsc(cardList)

  let kingNum = 0
  const hasSmallKing = cardList.some(isSmallJoker)
  const hasBigKing = cardList.some(isBigJoker)
  if (hasSmallKing) kingNum++
  if (hasBigKing) kingNum++
  const king = hasSmallKing ? cardList.find(isSmallJoker) : cardList.find(isBigJoker)
  const smallKing = cardList.find(isSmallJoker)
  const bigKing = cardList.find(isBigJoker)

  const faceCount = {}
  cardList.forEach(c => {
    if (c.isJoker) return
    const face = getFaceValue(c)
    faceCount[face] = (faceCount[face] || 0) + 1
  })

  // Object.keys 对数字键按升序返回，必须再按点数从大到小排：
  // 否则两对会先吐出最小两对，默认推荐就会把弱两对放到尾道、强两对放到中道（倒水）
  const byFaceDesc = (a, b) => b - a
  const fiveNum = Object.keys(faceCount).filter(k => faceCount[k] >= 5).map(Number).sort(byFaceDesc)
  const fourNum = Object.keys(faceCount).filter(k => faceCount[k] >= 4).map(Number).sort(byFaceDesc)
  const threeNum = Object.keys(faceCount).filter(k => faceCount[k] >= 3).map(Number).sort(byFaceDesc)
  const twoNum = Object.keys(faceCount).filter(k => faceCount[k] >= 2).map(Number).sort(byFaceDesc)

  // ============ 五炸 ============
  for (const fiveItem of fiveNum) {
    const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === fiveItem).slice(0, 5)
    typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.FiveBomb })
  }

  if (kingNum >= 1) {
    for (const fourItem of fourNum) {
      const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === fourItem).slice(0, 4)
      if (tempList.length === 4) {
        tempList.push(king)
        typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.FiveBomb })
      }
    }
  }

  if (kingNum === 2) {
    for (const threeItem of threeNum) {
      const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === threeItem).slice(0, 3)
      if (tempList.length === 3 && smallKing && bigKing) {
        tempList.push(smallKing)
        tempList.push(bigKing)
        typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.FiveBomb })
      }
    }
  }

  // ============ 同花顺 ============
  for (const item of sortedAsc) {
    if (item.isJoker) continue
    const itemRank = getFaceValue(item)
    if (itemRank >= 11) break
    
    const tempList = [item]
    const suit = item.suit.name
    
    for (const item2 of sortedAsc) {
      if (item2.isJoker) continue
      if (item2.suit.name !== suit) continue
      
      const item2Rank = getFaceValue(item2)
      const lastRank = getFaceValue(tempList[tempList.length - 1])
      if (item2Rank === lastRank + 1) {
        tempList.push(item2)
      }
      
      if (tempList.length >= 5) {
        typeCardList.push({ cardList: [...tempList], cardType: DeckTypeEnum.TongHuaShun })
        break
      }
    }
  }

  if (kingNum >= 1) {
    for (const item of sortedAsc) {
      if (item.isJoker) continue
      const itemRank = getFaceValue(item)
      if (itemRank >= 12) break
      
      const tempList = [item]
      const suit = item.suit.name
      
      for (const item2 of sortedAsc) {
        if (item2.isJoker) continue
        if (item2.suit.name !== suit) continue
        
        const item2Rank = getFaceValue(item2)
        const lastRank = getFaceValue(tempList[tempList.length - 1])
        if (item2Rank === lastRank + 1) {
          tempList.push(item2)
        }
        
        if (tempList.length >= 4) {
          tempList.push(king)
          typeCardList.push({ cardList: [...tempList], cardType: DeckTypeEnum.TongHuaShun })
          break
        }
      }
    }
  }

  if (kingNum === 2) {
    for (const item of sortedAsc) {
      if (item.isJoker) continue
      const itemRank = getFaceValue(item)
      if (itemRank >= 13) break
      
      const tempList = [item]
      const suit = item.suit.name
      
      for (const item2 of sortedAsc) {
        if (item2.isJoker) continue
        if (item2.suit.name !== suit) continue
        
        const item2Rank = getFaceValue(item2)
        const lastRank = getFaceValue(tempList[tempList.length - 1])
        if (item2Rank === lastRank + 1) {
          tempList.push(item2)
        }
        
        if (tempList.length >= 3 && smallKing && bigKing) {
          tempList.push(smallKing)
          tempList.push(bigKing)
          typeCardList.push({ cardList: [...tempList], cardType: DeckTypeEnum.TongHuaShun })
          break
        }
      }
    }
  }

  // ============ 炸弹 ============
  for (const fourItem of fourNum) {
    const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === fourItem).slice(0, 4)
    typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.Bomb })
  }

  if (kingNum >= 1) {
    for (const threeItem of threeNum) {
      const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === threeItem).slice(0, 3)
      if (tempList.length === 3) {
        tempList.push(king)
        typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.Bomb })
      }
    }
  }

  if (kingNum === 2) {
    for (const twoItem of twoNum) {
      const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === twoItem).slice(0, 2)
      if (tempList.length === 2 && smallKing && bigKing) {
        tempList.push(smallKing)
        tempList.push(bigKing)
        typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.Bomb })
      }
    }
  }

  // ============ 葫芦（对齐 g13suanfa：每段只取首个可用组合） ============
  for (const threeItem of threeNum) {
    const baseList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === threeItem).slice(0, 3)
    let added = false
    for (const twoItem of twoNum) {
      if (threeItem === twoItem) continue
      const pairCards = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === twoItem).slice(0, 2)
      if (pairCards.length >= 2) {
        typeCardList.push({ cardList: [...baseList, ...pairCards], cardType: DeckTypeEnum.Gourd })
        added = true
        break
      }
    }
    if (added) break
  }

  if (kingNum >= 1) {
    for (const pairItem of twoNum) {
      const baseList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === pairItem).slice(0, 2)
      let added = false
      for (const twoItem of twoNum) {
        if (pairItem === twoItem) continue
        const pairCards = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === twoItem).slice(0, 2)
        if (pairCards.length >= 2) {
          typeCardList.push({ cardList: [...baseList, ...pairCards, king], cardType: DeckTypeEnum.Gourd })
          added = true
          break
        }
      }
      if (added) break
    }
  }

  // ============ 同花 ============
  const suitCount = {}
  cardList.forEach(c => {
    if (c.isJoker) return
    const suit = c.suit.name
    suitCount[suit] = (suitCount[suit] || 0) + 1
  })

  // 花色顺序对齐 g13suanfa：1红桃 2黑桃 3梅花 4方块
  const suitOrder = ['hearts', 'spades', 'clubs', 'diamonds']
  const fiveColor = suitOrder.filter(k => (suitCount[k] || 0) >= 5)
  const fourColor = suitOrder.filter(k => (suitCount[k] || 0) >= 4)
  const threeColor = suitOrder.filter(k => (suitCount[k] || 0) >= 3)

  for (const fiveItem of fiveColor) {
    const tempList = sortedDesc.filter(c => !c.isJoker && c.suit.name === fiveItem)
    while (tempList.length >= 5) {
      typeCardList.push({ cardList: tempList.slice(0, 5), cardType: DeckTypeEnum.TongHua })
      tempList.shift()
    }
  }

  if (kingNum >= 1) {
    for (const fourItem of fourColor) {
      const tempList = sortedDesc.filter(c => !c.isJoker && c.suit.name === fourItem)
      while (tempList.length >= 4) {
        typeCardList.push({ cardList: tempList.slice(0, 4).concat([king]), cardType: DeckTypeEnum.TongHua })
        tempList.shift()
      }
    }
  }

  if (kingNum === 2) {
    for (const threeItem of threeColor) {
      const tempList = sortedDesc.filter(c => !c.isJoker && c.suit.name === threeItem)
      while (tempList.length >= 3 && smallKing && bigKing) {
        typeCardList.push({ cardList: tempList.slice(0, 3).concat([smallKing, bigKing]), cardType: DeckTypeEnum.TongHua })
        tempList.shift()
      }
    }
  }

  // ============ 顺子 ============
  for (const maxItem of sortedDesc) {
    if (maxItem.isJoker) continue
    const maxRank = getFaceValue(maxItem)
    if (maxRank <= 5) break
    
    const tempList = [maxItem]
    while (tempList.length < 5) {
      let found = false
      for (const item of sortedDesc) {
        if (item.isJoker) continue
        if (tempList.some(c => isSameCard(c, item))) continue
        
        const itemRank = getFaceValue(item)
        const lastRank = getFaceValue(tempList[tempList.length - 1])
        if (itemRank + 1 === lastRank) {
          tempList.push(item)
          found = true
          break
        }
      }
      if (!found) break
    }
    if (tempList.length >= 5) {
      typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.ShunZi })
    }
  }

  if (kingNum >= 1) {
    for (const maxItem of sortedDesc) {
      if (maxItem.isJoker) continue
      const maxRank = getFaceValue(maxItem)
      if (maxRank <= 4) break
      
      const tempList = [maxItem]
      while (tempList.length < 4) {
        let found = false
        for (const item of sortedDesc) {
          if (item.isJoker) continue
          if (tempList.some(c => isSameCard(c, item))) continue
          
          const itemRank = getFaceValue(item)
          const lastRank = getFaceValue(tempList[tempList.length - 1])
          if (itemRank + 1 === lastRank) {
            tempList.push(item)
            found = true
            break
          }
        }
        if (!found) break
      }
      tempList.push(king)
      if (tempList.length >= 5) {
        typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.ShunZi })
      }
    }
  }

  if (kingNum === 2) {
    for (const maxItem of sortedDesc) {
      if (maxItem.isJoker) continue
      const maxRank = getFaceValue(maxItem)
      if (maxRank <= 3) break
      
      const tempList = [maxItem]
      while (tempList.length < 3) {
        let found = false
        for (const item of sortedDesc) {
          if (item.isJoker) continue
          if (tempList.some(c => isSameCard(c, item))) continue
          
          const itemRank = getFaceValue(item)
          const lastRank = getFaceValue(tempList[tempList.length - 1])
          if (itemRank + 1 === lastRank) {
            tempList.push(item)
            found = true
            break
          }
        }
        if (!found) break
      }
      if (smallKing && bigKing) {
        tempList.push(smallKing)
        tempList.push(bigKing)
      }
      if (tempList.length >= 5) {
        typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.ShunZi })
      }
    }
  }

  // ============ 三张 ============
  for (const threeItem of threeNum) {
    const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === threeItem).slice(0, 3)
    typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.Three })
  }

  if (kingNum >= 1) {
    for (const twoItem of twoNum) {
      const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === twoItem).slice(0, 2)
      if (tempList.length === 2) {
        tempList.push(king)
        typeCardList.push({ cardList: tempList, cardType: DeckTypeEnum.Three })
      }
    }
  }

  if (kingNum === 2 && smallKing && bigKing) {
    for (const item of sortedDesc) {
      if (!item.isJoker) {
        typeCardList.push({ cardList: [item, smallKing, bigKing], cardType: DeckTypeEnum.Three })
        break
      }
    }
  }

  // ============ 两对 ============
  for (let i = 0; i < twoNum.length; i++) {
    const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === twoNum[i])
    const tempListCopy = [...tempList]
    while (tempListCopy.length >= 2) {
      for (let j = i + 1; j < twoNum.length; j++) {
        const tempList2 = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === twoNum[j])
        const tempList2Copy = [...tempList2]
        while (tempList2Copy.length >= 2) {
          typeCardList.push({
            cardList: tempListCopy.slice(0, 2).concat(tempList2Copy.slice(0, 2)),
            cardType: DeckTypeEnum.TwoDouble
          })
          tempList2Copy.shift()
        }
      }
      tempListCopy.shift()
    }
  }

  // ============ 对子 ============
  for (const twoItem of twoNum) {
    const tempList = sortedDesc.filter(c => !c.isJoker && getFaceValue(c) === twoItem)
    const tempListCopy = [...tempList]
    while (tempListCopy.length >= 2) {
      typeCardList.push({ cardList: tempListCopy.slice(0, 2), cardType: DeckTypeEnum.Double })
      tempListCopy.shift()
    }
  }

  if (kingNum >= 1) {
    for (const item of sortedDesc) {
      if (!item.isJoker) {
        typeCardList.push({ cardList: [item, king], cardType: DeckTypeEnum.Double })
        break
      }
    }
  }

  // ============ 乌龙 (取前三张) ============
  if (cardList.length >= 3) {
    typeCardList.push({ cardList: sortedDesc.slice(0, 3), cardType: DeckTypeEnum.Single })
  }

  // 与 g13suanfa 一致：按插入顺序（高牌型优先写入），不再二次排序打乱 index 语义
  return typeCardList
}

/**
 * 同类型牌型比较：curr 是否强于或等于 last（中道不能强于尾道 / 头道不能强于中道）
 * 对齐 g13suanfa，但花色不计大小（只逐张比点数；鬼牌数量相同时视为相等）
 */
function isHandStrongerOrEqual(currHand, lastHand) {
  const currFaces = currHand
    .filter(c => !c.isJoker)
    .map(getFaceValue)
    .sort((a, b) => b - a)
  const lastFaces = lastHand
    .filter(c => !c.isJoker)
    .map(getFaceValue)
    .sort((a, b) => b - a)

  const minSize = Math.min(currFaces.length, lastFaces.length)
  for (let i = 0; i < minSize; i++) {
    if (currFaces[i] !== lastFaces[i]) {
      return currFaces[i] > lastFaces[i]
    }
  }

  const currJokerCount = currHand.filter(c => c.isJoker).length
  const lastJokerCount = lastHand.filter(c => c.isJoker).length
  if (currJokerCount !== lastJokerCount) {
    // 非鬼牌面值相同：鬼少的一方更强（鬼在填空，天然高张更多）
    return currJokerCount < lastJokerCount
  }

  // 点数与鬼数都相同：花色无大小，视为相等（不算更强）
  return false
}

/** 把每道补到规定张数（尾/中 5 张，头 3 张） */
function fillIncompleteRows(cm, remain) {
  for (let i = 0; i < cm.typeCardList.length; i++) {
    const need = i === 2 ? 3 : 5
    while (cm.typeCardList[i].cardList.length < need) {
      if (remain.length === 0) break
      cm.typeCardList[i].cardList.push(remain.shift())
    }
  }
}

/**
 * 补齐后若中道仍大于尾道（两对比较应对大对/小对/踢脚，不能只看最大单张），对调中尾。
 */
function ensureBackBeatsMiddle(cm) {
  if (!cm.typeCardList || cm.typeCardList.length < 2) return
  const back = cm.typeCardList[0]
  const middle = cm.typeCardList[1]
  if (!back?.cardList || !middle?.cardList) return
  if (back.cardList.length !== 5 || middle.cardList.length !== 5) return
  if (compareFiveCards(middle.cardList, back.cardList) > 0) {
    cm.typeCardList[0] = middle
    cm.typeCardList[1] = back
  }
}

/**
 * 推荐方案去重（对齐 g13suanfa deduplicateResults）
 */
function deduplicateResults(cmlist) {
  const seen = new Set()
  const unique = []
  for (const cm of cmlist) {
    const parts = cm.typeCardList.map(tc => {
      const ids = tc.cardList.map(c => c.id).slice().sort()
      return `${tc.cardType}:${ids.join(',')}`
    })
    const key = parts.join('|')
    if (!seen.has(key)) {
      seen.add(key)
      unique.push(cm)
    }
  }
  return unique
}

/**
 * 递归获取所有自动牌型排列（对齐 g13suanfa GetAllResult）
 * @param {Card[]} cardList - 手牌
 * @returns {object[]} 所有排列
 */
function getAllResult(cardList) {
  const cmlist = []

  function getAll(cmlist, cards) {
    let cm = { typeCardList: [] }
    let typeCardList
    let newCardlist = [...cards]

    // 第一个方案：每墩取当前最强牌型
    if (cmlist.length === 0) {
      while (true) {
        typeCardList = getMaxCardType(newCardlist)
        if (!typeCardList.length) break

        if (cm.typeCardList.length === 2 && typeCardList[0].cardList.length > 3) {
          typeCardList = getMaxCardType(typeCardList[0].cardList.slice(0, 3))
        }

        cm.typeCardList.push(typeCardList[0])
        deleteListElement(newCardlist, typeCardList[0].cardList)

        if (cm.typeCardList.length >= 3) {
          fillIncompleteRows(cm, newCardlist)
          ensureBackBeatsMiddle(cm)
          break
        }
      }

      cmlist.push(cm)
      getAll(cmlist, cards)
    }

    // 其他方案：重新初始化牌组副本和牌型，避免使用第一个分支中已消耗的数据
    newCardlist = [...cards]
    cm = { typeCardList: [] }

    if (cmlist.length < 4) {
      let singleCount = 0
      while (true) {
        typeCardList = getMaxCardType(newCardlist)
        const index = cmlist.length
        let typeCardListIndex = 0

        if (cm.typeCardList.length === 0) {
          if (typeCardList.length <= index) {
            return deduplicateResults(cmlist)
          }
          cm.typeCardList.push(typeCardList[index])
          deleteListElement(newCardlist, typeCardList[index].cardList)
        } else {
          while (true) {
            if (typeCardListIndex >= typeCardList.length) {
              return deduplicateResults(cmlist)
            }

            const curr = typeCardList[typeCardListIndex]
            const last = cm.typeCardList[cm.typeCardList.length - 1]

            if (curr.cardType > last.cardType) {
              typeCardListIndex++
              continue
            }

            if (curr.cardType === last.cardType) {
              // 同类型且前道不弱于后道：跳过该组合，改试下一组（不要整表结束）
              if (isHandStrongerOrEqual(curr.cardList, last.cardList)) {
                typeCardListIndex++
                continue
              }
            }

            break
          }

          if (cm.typeCardList.length === 2 && typeCardList[typeCardListIndex].cardList.length > 3) {
            const reduced = typeCardList[typeCardListIndex].cardList.slice(0, 3)
            const reducedTypes = getMaxCardType(reduced)
            if (reducedTypes.length > 0) {
              cm.typeCardList.push(reducedTypes[0])
              deleteListElement(newCardlist, reducedTypes[0].cardList)
            } else {
              cm.typeCardList.push({ cardList: reduced, cardType: DeckTypeEnum.Single })
              deleteListElement(newCardlist, reduced)
            }
          } else {
            cm.typeCardList.push(typeCardList[typeCardListIndex])
            deleteListElement(newCardlist, typeCardList[typeCardListIndex].cardList)
          }
        }

        if (cm.typeCardList.length >= 3) {
          fillIncompleteRows(cm, newCardlist)
          ensureBackBeatsMiddle(cm)
          for (let i = 0; i < cm.typeCardList.length; i++) {
            if (cm.typeCardList[i].cardType === DeckTypeEnum.Single) {
              singleCount++
            }
          }
          break
        }
      }

      cmlist.push(cm)

      if (singleCount >= 2) {
        return deduplicateResults(cmlist)
      }

      getAll(cmlist, cards)
    }

    return deduplicateResults(cmlist)
  }

  return getAll(cmlist, cardList)
}

// ==================== 原有辅助函数 ====================

// 保守摆牌策略
function tryConservativeLayout(hands) {
  const sorted = sortByRank(hands)

  const back = sorted.slice(0, 5)
  const middle = sorted.slice(5, 10)
  const front = sorted.slice(10, 13)

  let layout = { front, middle, back }
  let valid = isValidLayout(front, middle, back)

  if (!valid.valid) {
    const weakCards = sorted.slice(7, 13)

    for (let i = 0; i <= 3; i++) {
      for (let j = i + 1; j <= 4; j++) {
        for (let k = j + 1; k <= 5; k++) {
          const testFront = [weakCards[i], weakCards[j], weakCards[k]]
          const remaining = sorted.filter(c => !testFront.includes(c))
          const testBack = remaining.slice(0, 5)
          const testMiddle = remaining.slice(5, 10)

          const testValid = isValidLayout(testFront, testMiddle, testBack)
          if (testValid.valid) {
            return { front: testFront, middle: testMiddle, back: testBack }
          }
        }
      }
    }
  }

  return layout
}

// 强制生成合法摆牌
function forceValidLayout(hands) {
  const sorted = sortByRank(hands)

  for (let backStart = 0; backStart <= 8; backStart++) {
    for (let middleStart = backStart + 5; middleStart <= 10; middleStart++) {
      const back = sorted.slice(backStart, backStart + 5)
      const middle = sorted.slice(middleStart, middleStart + 5)
      const front = sorted.filter(c => !back.includes(c) && !middle.includes(c)).slice(0, 3)

      if (front.length === 3 && middle.length === 5 && back.length === 5) {
        const result = isValidLayout(front, middle, back)
        if (result.valid) {
          return { front, middle, back }
        }
      }
    }
  }

  return {
    front: sorted.slice(10, 13),
    middle: sorted.slice(5, 10),
    back: sorted.slice(0, 5)
  }
}

// 选择最佳候选方案
function selectBestCandidate(candidates) {
  if (candidates.length === 0) {
    return null
  }
  
  if (candidates.length === 1) {
    return candidates[0]
  }
  
  const scored = candidates.map(layout => {
    const score = evaluateLayout(layout)
    return { layout, score }
  })
  
  scored.sort((a, b) => b.score - a.score)
  
  const best = scored[0].layout
  if (isValidLayout(best.front, best.middle, best.back).valid) {
    return best
  }
  
  for (const item of scored) {
    if (isValidLayout(item.layout.front, item.layout.middle, item.layout.back).valid) {
      return item.layout
    }
  }
  
  return null
}

// 评估摆牌方案的得分
function evaluateLayout(layout) {
  const { front, middle, back } = layout

  const frontEval = evaluateThreeCards(front)
  const middleEval = evaluateFiveCards(middle)
  const backEval = evaluateFiveCards(back)

  let score = 0

  score += frontEval.rank.value * 2
  score += middleEval.rank.value * 3
  score += backEval.rank.value * 4

  let strongRows = 0
  if (frontEval.rank.value >= 2) strongRows++
  if (middleEval.rank.value >= 2) strongRows++
  if (backEval.rank.value >= 2) strongRows++
  score += strongRows * 8

  if (strongRows === 3) score += 12

  const frontResolved = frontEval.originalCards ? frontEval.cards : front
  const middleResolved = middleEval.originalCards ? middleEval.cards : middle
  const backResolved = backEval.originalCards ? backEval.cards : back
  const allCards = [...frontResolved, ...middleResolved, ...backResolved]
  const rankCounts = getRankCounts(allCards)
  Object.entries(rankCounts).forEach(([rank, count]) => {
    if (count >= 3) {
      const r = parseInt(rank)
      const frontCount = frontResolved.filter(c => c.rank.value === r).length
      const middleCount = middleResolved.filter(c => c.rank.value === r).length
      const backCount = backResolved.filter(c => c.rank.value === r).length
      const groups = [frontCount, middleCount, backCount].filter(c => c > 0).length
      if (groups === 1) {
        score += count === 4 ? 50 : 30
      } else if (groups === 2) {
        score -= 60
      } else {
        score -= 100
      }
    }
  })

  const avg = (frontEval.rank.value + middleEval.rank.value + backEval.rank.value) / 3
  score += avg * 2

  if (Number(frontEval.rank.value) === 1) score -= 2

  return score
}

// 获取前N个最佳摆牌方案（顺序对齐 g13suanfa GetAllResult）
export function getTopArrangements(hands, count = 3) {
  const results = getAllResult(hands)
  
  if (results.length === 0) {
    return [{ layout: forceValidLayout(hands), score: 0 }]
  }

  const typeName = (t) => {
    const map = {
      [DeckTypeEnum.Single]: '乌龙',
      [DeckTypeEnum.Double]: '对子',
      [DeckTypeEnum.TwoDouble]: '两对',
      [DeckTypeEnum.Three]: '三条',
      [DeckTypeEnum.ShunZi]: '顺子',
      [DeckTypeEnum.TongHua]: '同花',
      [DeckTypeEnum.Gourd]: '葫芦',
      [DeckTypeEnum.Bomb]: '炸弹',
      [DeckTypeEnum.TongHuaShun]: '同花顺',
      [DeckTypeEnum.FiveBomb]: '五同'
    }
    return map[t] || String(t)
  }

  const arrangements = []
  const seen = new Set()

  for (const r of results) {
    if (arrangements.length >= count) break
    if (!r.typeCardList || r.typeCardList.length < 3) continue

    const layout = {
      front: r.typeCardList[2].cardList,
      middle: r.typeCardList[1].cardList,
      back: r.typeCardList[0].cardList
    }

    // 补齐后校验数量
    if (layout.front.length !== 3 || layout.middle.length !== 5 || layout.back.length !== 5) {
      continue
    }

    if (!isValidLayout(layout.front, layout.middle, layout.back).valid) {
      continue
    }

    const key = [
      layout.front.map(c => c.id).sort().join(','),
      layout.middle.map(c => c.id).sort().join(','),
      layout.back.map(c => c.id).sort().join(',')
    ].join('|')
    if (seen.has(key)) continue
    seen.add(key)

    arrangements.push({
      layout,
      score: evaluateLayout(layout),
      // 便于调试：与 g13suanfa 输出一致的牌型标签
      types: [
        typeName(r.typeCardList[2].cardType),
        typeName(r.typeCardList[1].cardType),
        typeName(r.typeCardList[0].cardType)
      ]
    })
  }

  if (arrangements.length === 0) {
    return [{ layout: forceValidLayout(hands), score: 0 }]
  }

  return arrangements
}

// 模拟AI思考时间
export function getAIThinkTime() {
  return 1500 + Math.random() * 1500
}